import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import '../ConsultoriaMicrosoftAzurePage/ConsultoriaMicrosoftAzurePage.css';

const PAGE_URL = 'https://e-asy.es/servicios/azure';
const HUB_URL = '/servicios/consultoria-microsoft-azure';

const FAQ_ITEMS = [
  {
    question: '¿Sois partner oficial de Azure?',
    answer:
      'Estamos en el roadmap de Microsoft Solutions Partner. Es una especialización en curso, no un sello que vendamos como ya obtenido. El trabajo de infraestructura de proyectos en Azure sí lo hacemos hoy.',
  },
  {
    question: '¿Esto es lo mismo que Microsoft 365?',
    answer:
      'No. 365 es correo, identidad y archivos del día a día. Azure es lo que hay que alojar: entornos, APIs, aplicaciones, copias y costes. Suelen ir juntos, pero no es un alta de Outlook.',
  },
  {
    question: '¿Y si lo que necesitamos es Kubernetes y CI/CD?',
    answer:
      'Esa ficha es Infraestructura cloud y DevOps. Aquí somos el equipo tecnológico de la pyme: entornos de proyecto, accesos y operación, sin montar un departamento interno ni un “landing zone” de consultora grande.',
  },
  {
    question: '¿Podéis alojar en Azure la aplicación de IA que ya tenemos?',
    answer:
      'El suelo cloud (suscripción, redes, accesos, costes) está en esta ficha. El backend, la API y el go-live del prototipo están en IA en producción. Se encadenan; no son el mismo servicio.',
  },
  {
    question: '¿Solo Azure, o también AWS?',
    answer:
      'Azure es la línea por defecto en el entorno Microsoft. Si la carga ya está en Amazon o no pinta en Azure, ordenamos AWS con el mismo criterio: cuenta, identidad, costes visibles y operación.',
  },
];

const AzurePage = ({ darkMode, toggleDarkMode }) => {
  const bloques = [
    {
      titulo: 'Entornos de proyecto',
      descripcion:
        'La máquina, la API o el entorno donde corre el trabajo. No un PowerPoint de arquitectura.',
      puntos: [
        'Suscripción, redes y recursos que se pueden explicar',
        'Entorno de pruebas y de producción, no “lo subí yo un domingo”',
        'Copias que se pueden restaurar',
      ],
    },
    {
      titulo: 'Identidad, accesos y costes',
      descripcion:
        'Quién entra, con qué permiso y cuánto cuesta a final de mes. Visible para dirección, no solo para un técnico.',
      puntos: [
        'Cuentas de empresa, no correos personales mezclados',
        'Permisos por rol, no “lo tiene todo el mundo”',
        'Costes a la vista antes de que la factura sorprenda',
      ],
    },
    {
      titulo: 'Operación continua',
      descripcion:
        'Nos quedamos como equipo tecnológico externo: criterio y alguien que responde. Sin contratar un departamento IT.',
      puntos: [
        'Mantenimiento y cambios con responsable',
        'Handover documentado: no dependéis de un único perfil',
        'Misma línea que 365 y Business Central si encajan',
      ],
    },
  ];

  const paraQuien = [
    {
      titulo: 'Pyme sin equipo IT interno',
      descripcion:
        'Hay que alojar algo (una app, una API, un entorno) y no queréis un departamento de tres personas ni un partner que configura y desaparece.',
    },
    {
      titulo: 'Ya estáis en Azure, pero desordenado',
      descripcion:
        'Una suscripción que “ya está”, costes opacos y un técnico que se va de vacaciones. Hay que ordenar, no empezar de cero.',
    },
    {
      titulo: 'Queréis desplegar una app o un agente de IA',
      descripcion:
        'El prototipo existe. Falta el suelo en Azure. Coordinamos con IA en producción: ellos el backend; aquí la infra.',
    },
  ];

  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      url: PAGE_URL,
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    }),
    []
  );

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Consultoría Azure para pymes',
    description:
      'Infraestructura de proyectos en Microsoft Azure para pymes: entornos, identidad, costes visibles y operación como equipo tecnológico externo. Roadmap Microsoft Solutions Partner en curso.',
    url: PAGE_URL,
    serviceType: 'Consultoría Microsoft Azure',
    provider: {
      '@type': 'Organization',
      name: 'E-ASY',
      url: 'https://e-asy.es',
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Canarias' },
      { '@type': 'Country', name: 'España' },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://e-asy.es/' },
      { '@type': 'ListItem', position: 2, name: 'Servicios', item: 'https://e-asy.es/servicios' },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Consultoría Microsoft, Azure y partners',
        item: 'https://e-asy.es/servicios/consultoria-microsoft-azure',
      },
      { '@type': 'ListItem', position: 4, name: 'Consultoría Azure', item: PAGE_URL },
    ],
  };

  return (
    <>
      <SEO
        title="Consultoría Azure para pymes | Infra de proyectos | E-ASY"
        description="Especialistas Azure para pymes: entornos, accesos, costes y operación. No es Microsoft 365 ni Kubernetes. Equipo tecnológico externo, en remoto para toda España desde la sede en Tenerife."
        keywords="consultoría Azure pymes, especialistas Azure España, especialistas Azure Canarias, infraestructura Azure empresas, desplegar aplicación Azure, Azure sin departamento IT, E-ASY Tenerife"
        canonical={PAGE_URL}
      />
      <JsonLd id="jsonld-azure-faq" data={faqSchema} />
      <JsonLd id="jsonld-azure-service" data={serviceSchema} />
      <JsonLd id="jsonld-azure-breadcrumb" data={breadcrumbSchema} />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`partners-cloud-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="partners-hero">
          <div className="partners-hero-content">
            <div className="partners-hero-text">
              <h1 className="partners-hero-title">
                Consultoría Azure para pymes: infra de proyectos y operación
              </h1>
              <p className="partners-hero-subtitle">
                <strong>Especialistas Azure</strong> para empresas que tienen
                que alojar algo de verdad —una API, una app, un entorno, un
                agente de IA— y no un departamento IT interno. Entornos,
                accesos, costes a la vista y alguien que responde.
              </p>
              <p className="partners-hero-subtitle">
                No es un alta de Microsoft 365 ni un proyecto de Kubernetes.
                Roadmap de <strong>Microsoft Solutions Partner en curso</strong>
                : el trabajo, no el sello.
              </p>
              <div className="partners-hero-cta">
                <Link to="/contacto" className="partners-hero-button primary">
                  Agenda una consultoría gratuita
                </Link>
                <Link to={HUB_URL} className="partners-hero-button secondary">
                  Ver el hub Microsoft
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="partners-que-es">
          <div className="partners-que-es-content">
            <div className="partners-que-es-header">
              <h2 className="partners-que-es-title">Qué no es esta ficha</h2>
              <p className="partners-que-es-subtitle">
                Si lo que buscáis es correo, identidad y archivos, esa capa es{' '}
                <Link to={HUB_URL}>Microsoft 365</Link> en el hub. Si el cuello
                es contenedores, CI/CD y alta disponibilidad para un equipo que
                despliega software a diario, esa ficha es{' '}
                <Link to="/servicios/infraestructura-cloud-devops">
                  Infraestructura cloud y DevOps
                </Link>
                . Si ya tenéis un prototipo de IA y falta el backend,{' '}
                <Link to="/servicios/ia-en-produccion">IA en producción</Link>
                ; aquí montamos el suelo Azure.
              </p>
            </div>
          </div>
        </section>

        <section className="partners-servicios">
          <div className="partners-servicios-content">
            <div className="partners-servicios-header">
              <h2 className="partners-servicios-title">Qué cubre Azure con nosotros</h2>
              <p className="partners-servicios-subtitle">
                Infraestructura de proyecto a medida de una pyme. Sin teatro de
                “cloud nativo” ni landing zone de consultora grande.
              </p>
            </div>
            <div className="partners-servicios-grid partners-paquetes-grid">
              {bloques.map((item) => (
                <article key={item.titulo} className="partners-servicio-card">
                  <h3 className="servicio-title">{item.titulo}</h3>
                  <p className="servicio-description">{item.descripcion}</p>
                  <div className="servicio-casos">
                    <h4 className="casos-title">Incluye:</h4>
                    <ul className="casos-list">
                      {item.puntos.map((punto) => (
                        <li key={punto} className="caso-item">
                          {punto}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="partners-sectores">
          <div className="partners-sectores-content">
            <div className="partners-sectores-header">
              <h2 className="partners-sectores-title">Para quién</h2>
              <p className="partners-sectores-subtitle">
                Empresas en Canarias y el resto de España que necesitan Azure
                operado, no un tutorial. El ERP, si encaja Microsoft, está en{' '}
                <Link to="/servicios/business-central">Business Central</Link>
                ; si el dato es sociosanitario, en{' '}
                <Link to="/vulpin">Vulpin Suite</Link>.
              </p>
            </div>
            <div className="partners-sectores-grid">
              {paraQuien.map((item) => (
                <article key={item.titulo} className="partners-sector-card">
                  <h3 className="sector-title">{item.titulo}</h3>
                  <p className="sector-description">{item.descripcion}</p>
                </article>
              ))}
            </div>
            <p className="partners-ciber-callout">
              Esta ficha es hija del pilar{' '}
              <Link to={HUB_URL}>
                Consultoría Microsoft / Azure / Google Workspace
              </Link>
              : el equipo tecnológico externo. Azure es la capa de
              infraestructura de proyectos. Lectura:{' '}
              <Link to="/blog/equipo-azure-pyme-sin-departamento-it">
                qué hace un equipo Azure en una pyme sin IT
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="partners-faq">
          <div className="partners-faq-content">
            <div className="partners-faq-header">
              <h2 className="partners-faq-title">Preguntas frecuentes</h2>
            </div>
            <div className="partners-faq-list">
              {FAQ_ITEMS.map((item) => (
                <details key={item.question} className="partners-faq-item">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="partners-cta">
          <div className="partners-cta-content">
            <h2 className="partners-cta-title">
              ¿Hay que alojar algo en Azure y no tenéis quién lo opere?
            </h2>
            <p className="partners-cta-subtitle">
              Revisamos suscripción, entornos y costes. Un plan que se pueda
              llevar desde fuera.
            </p>
            <div className="partners-cta-buttons">
              <Link to="/contacto" className="partners-cta-button primary">
                Agenda una consultoría gratuita
              </Link>
              <Link to={HUB_URL} className="partners-cta-button secondary">
                Volver al hub Microsoft
              </Link>
            </div>
          </div>
        </section>
      </div>
      <CoberturaNota />
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default AzurePage;
